import { createFileRoute, Outlet } from "@tanstack/react-router";
import { z } from "zod";
import { EquipmentId, EquipmentStatusEnum } from "@brewbase/db/types";
import { trpc } from "@/hooks/trpc";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { useSidebarConfig } from "@/hooks/use-sidebar-config";
import type { SidebarNavItem } from "@/components/nav/sidebar-store";
import { useMemo } from "react";
import { AlertCircleIcon, Package } from "lucide-react";

export const Route = createFileRoute("/inventory/equipment/$equipment_id")({
  component: EquipmentLayout,
  params: z.object({
    equipment_id: z.coerce.number().transform((val) => val as EquipmentId),
  }),
});

function EquipmentLayout() {
  const { equipment_id } = Route.useParams();

  const {
    data: equipment,
    isLoading,
    error,
  } = trpc.equipment.get.useQuery(equipment_id);
  const { data: maintenanceLogs } =
    trpc.equipment.maintenanceLog.list.useQuery(equipment_id);
  const { data: cleaningRecords } =
    trpc.cleaning.records.list.useQuery({ equipmentId: equipment_id });

  const navigation = useMemo<SidebarNavItem[]>(
    () => [
      {
        type: "link",
        id: "general",
        label: "General",
        href: `/inventory/equipment/${equipment_id}`,
        exact: true,
      },
      {
        type: "link",
        id: "maintenance",
        label: "Maintenance",
        href: `/inventory/equipment/${equipment_id}/maintenance`,
        count: maintenanceLogs?.length || undefined,
      },
      {
        type: "link",
        id: "cleaning",
        label: "Cleaning",
        href: `/inventory/equipment/${equipment_id}/cleaning`,
        count: cleaningRecords?.length || undefined,
      },
    ],
    [equipment_id, maintenanceLogs?.length, cleaningRecords?.length],
  );

  useSidebarConfig({
    layer: 2,
    parent: { label: "Equipment", icon: Package, href: "/inventory/equipment" },
    header: equipment
      ? {
          title: equipment.name,
          subtitle: `${equipment.equipmentModel.equipmentType.name} · ${equipment.equipmentModel.name}`,
          status: (
            <Badge
              variant={
                equipment.status === EquipmentStatusEnum.ACTIVE
                  ? "default"
                  : equipment.status === EquipmentStatusEnum.INACTIVE
                    ? "secondary"
                    : "outline"
              }
            >
              {equipment.status}
            </Badge>
          ),
        }
      : undefined,
    navigation,
  });

  if (isLoading) {
    return <EquipmentLayoutSkeleton />;
  }

  if (error || !equipment) {
    return (
      <div className="flex items-center justify-center h-full p-8">
        <div className="text-center">
          <AlertCircleIcon className="w-10 h-10 mx-auto text-destructive mb-2" />
          <p className="text-sm text-muted-foreground">
            {error?.message ?? "Equipment not found"}
          </p>
        </div>
      </div>
    );
  }

  return (
    <main className="flex-1 min-h-0 overflow-auto h-full">
      <Outlet />
    </main>
  );
}

function EquipmentLayoutSkeleton() {
  return (
    <div className="flex-1 p-6 space-y-4">
      <Skeleton className="h-5 w-32" />
      <Skeleton className="h-8 w-48" />
      <Skeleton className="h-64 w-full" />
    </div>
  );
}
